import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sun, ShieldAlert, Sparkles, CalendarClock, Bell } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter D.1 — Solar Property Intelligence card.
 *
 * Renders on CustomerProfile when a property is known. Reads
 * `/api/properties/{property_id}/solar-intel` which aggregates the Solar
 * summary from existing measurements + returns saved safety / reminder /
 * ai / history state and deterministic service opportunities.
 *
 * This surface is deliberately additive — no measurement or estimate flow
 * is modified. All writes go through PUT solar-intel + POST history.
 */
const SAFETY_FLAGS = [
  { key: "fragile_panels",     label: "Fragile panels" },
  { key: "steep_roof",         label: "Steep roof" },
  { key: "limited_access",     label: "Limited access" },
  { key: "bird_activity",      label: "Bird activity" },
  { key: "heavy_debris",       label: "Heavy debris" },
  { key: "hand_cleaning_only", label: "Customer requested hand cleaning" },
  { key: "no_pressure_wash",   label: "Do not pressure wash" },
];

const REMINDER_OPTIONS = [
  { key: "6_months",  label: "Every 6 months" },
  { key: "12_months", label: "Every 12 months" },
  { key: "custom",    label: "Custom interval" },
];

export default function PropertySolarCard({ propertyId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyDraft, setHistoryDraft] = useState({ kind: "cleaning", notes: "" });

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const r = await api.get(`/properties/${propertyId}/solar-intel`);
        setData(r.data);
      } catch { setData(null); }
    })();
  }, [propertyId]);

  if (!propertyId) return null;
  if (!data) return null;
  const solarPresent = data?.summary?.present;

  const savePartial = async (patch) => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put(`/properties/${propertyId}/solar-intel`, patch);
      setData((d) => ({ ...d, ...fresh }));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleFlag = (key) => {
    const cur = new Set(data?.safety?.flags || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    savePartial({ safety: { flags: Array.from(cur), notes: data?.safety?.notes || null } });
  };

  const saveSafetyNotes = (notes) => {
    savePartial({ safety: { flags: data?.safety?.flags || [], notes } });
  };

  const saveReminder = (patch) => {
    savePartial({ reminder: { ...(data?.reminder || {}), ...patch } });
  };

  const appendHistory = async () => {
    if (!historyDraft.kind) return;
    setSaving(true);
    try {
      const { data: fresh } = await api.post(`/properties/${propertyId}/solar-intel/history`, historyDraft);
      setData((d) => ({ ...d, ...fresh }));
      setHistoryDraft({ kind: "cleaning", notes: "" });
      toast.success("Service entry logged");
    } catch { toast.error("Log failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="property-solar-card">
      <div className="flex items-center gap-2 mb-4">
        <Sun className="w-5 h-5 text-amber-500" />
        <h2 className="font-heading text-xl font-bold">Solar Property Intelligence</h2>
        {solarPresent ? (
          <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="property-solar-present-chip">
            Solar detected
          </span>
        ) : (
          <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50 border border-zinc-200 rounded px-2 py-0.5" data-testid="property-solar-absent-chip">
            No solar yet
          </span>
        )}
      </div>

      {solarPresent ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="property-solar-summary">
            <SummaryTile label="Array area" value={`${Math.round(data.summary.total_area_sqft).toLocaleString()} sq ft`} testid="summary-area" />
            <SummaryTile label="Panels (est.)" value={data.summary.estimated_panel_count ?? "—"} testid="summary-panels" />
            <SummaryTile label="Orientation" value={data.summary.orientation ? `${data.summary.orientation}°` : "—"} testid="summary-orientation" />
            <SummaryTile label="Confidence" value={data.summary.confidence || "—"} testid="summary-confidence" />
          </div>
          <div className="text-xs text-zinc-500 mb-6" data-testid="property-solar-date">
            {data.summary.date_analyzed ? `Analyzed ${new Date(data.summary.date_analyzed).toLocaleDateString()}` : "Not yet analyzed"}
            {(data.summary.measurement_ids?.length || 0) > 0 && (
              <> · {data.summary.measurement_ids.length} solar measurement(s) on file</>
            )}
          </div>

          {/* Opportunities */}
          <div className="mb-6" data-testid="property-solar-opportunities">
            <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold">
              <Sparkles className="w-4 h-4 text-[#FF5A00]" /> Recommended services
            </div>
            <ul className="space-y-1.5">
              {(data.opportunities || []).map((o) => (
                <li key={o.key} data-testid={`opportunity-${o.key}`}
                    className="text-sm border border-zinc-200 rounded p-2 hover:border-[#FF5A00] transition-colors">
                  <div className="font-medium">{o.label}</div>
                  <div className="text-xs text-zinc-500">{o.rationale}</div>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div className="text-sm text-zinc-500 mb-6" data-testid="property-solar-empty">
          No solar array has been measured on this property yet. Open the Measurement Workspace and use the Solar review card to analyze the roof.
        </div>
      )}

      {/* Safety flags */}
      <div className="mb-6" data-testid="property-solar-safety">
        <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold">
          <ShieldAlert className="w-4 h-4 text-rose-500" /> Safety notes
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          {SAFETY_FLAGS.map((f) => {
            const checked = (data?.safety?.flags || []).includes(f.key);
            return (
              <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleFlag(f.key)}
                  disabled={saving}
                  data-testid={`safety-flag-${f.key}`}
                />
                <span>{f.label}</span>
              </label>
            );
          })}
        </div>
        <Textarea
          className="mt-1"
          placeholder="Additional safety notes (permanent)…"
          defaultValue={data?.safety?.notes || ""}
          onBlur={(e) => e.target.value !== (data?.safety?.notes || "") && saveSafetyNotes(e.target.value)}
          data-testid="safety-notes-input"
        />
      </div>

      {/* Reminder framework — no automation, saves model only */}
      <div className="mb-6" data-testid="property-solar-reminder">
        <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold">
          <Bell className="w-4 h-4 text-amber-500" /> Maintenance reminder <span className="text-[10px] uppercase tracking-widest text-zinc-400">(model only)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={data?.reminder?.interval || undefined}
            onValueChange={(v) => saveReminder({ interval: v })}
          >
            <SelectTrigger className="w-52" data-testid="reminder-interval-select">
              <SelectValue placeholder="Choose an interval" />
            </SelectTrigger>
            <SelectContent>
              {REMINDER_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key} data-testid={`reminder-option-${o.key}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data?.reminder?.interval === "custom" && (
            <Input
              type="number" placeholder="Days"
              className="w-24"
              defaultValue={data?.reminder?.custom_days || ""}
              onBlur={(e) => saveReminder({ custom_days: e.target.value ? Number(e.target.value) : null })}
              data-testid="reminder-custom-days"
            />
          )}
          <label className="flex items-center gap-1 text-sm">
            <Checkbox
              checked={!!data?.reminder?.enabled}
              onCheckedChange={(v) => saveReminder({ enabled: !!v })}
              data-testid="reminder-enabled"
            />
            <span>Enabled</span>
          </label>
        </div>
      </div>

      {/* Service history */}
      <div data-testid="property-solar-history">
        <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold">
          <CalendarClock className="w-4 h-4 text-emerald-600" /> Service history
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div className="w-40">
            <Label className="text-xs">Kind</Label>
            <Select value={historyDraft.kind} onValueChange={(v) => setHistoryDraft((d) => ({ ...d, kind: v }))}>
              <SelectTrigger data-testid="history-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cleaning">Cleaning</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Notes</Label>
            <Input
              value={historyDraft.notes}
              onChange={(e) => setHistoryDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Optional notes"
              data-testid="history-notes-input"
            />
          </div>
          <Button
            onClick={appendHistory}
            disabled={saving}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="history-add-btn"
          >
            Log service
          </Button>
        </div>
        {(data?.service_history || []).length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="history-empty">No service entries yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {(data.service_history || []).map((e) => (
              <li key={e.id || e.date} className="border border-zinc-200 rounded p-2 text-sm" data-testid={`history-entry-${e.id || e.date}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium capitalize flex items-center gap-1">
                    {e.kind}
                    {e.auto && <span className="text-[9px] uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-200 rounded px-1" data-testid={`history-auto-${e.id}`}>Auto</span>}
                  </span>
                  <span className="text-[11px] text-zinc-500 font-mono-accent">
                    {e.date ? new Date(e.date).toLocaleDateString() : ""}
                  </span>
                </div>
                {e.notes && <div className="text-xs text-zinc-600">{e.notes}</div>}
                {e.performed_by && <div className="text-[11px] text-zinc-500">by {e.performed_by}</div>}
                {(e.linked_estimate || e.linked_job || e.linked_invoice) && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`history-links-${e.id}`}>
                    {e.linked_estimate && (
                      <LinkChip kind="Estimate" record={e.linked_estimate} href={`/app/estimates/${e.linked_estimate.id}`} testid={`history-link-estimate-${e.id}`} />
                    )}
                    {e.linked_job && (
                      <LinkChip kind="Job" record={e.linked_job} href={`/app/jobs/${e.linked_job.id}`} testid={`history-link-job-${e.id}`} />
                    )}
                    {e.linked_invoice && (
                      <LinkChip kind="Invoice" record={e.linked_invoice} href={`/app/invoices/${e.linked_invoice.id}`} testid={`history-link-invoice-${e.id}`} />
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  draft:     "bg-zinc-100 text-zinc-700 border-zinc-200",
  sent:      "bg-blue-50 text-blue-800 border-blue-200",
  accepted:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  scheduled: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  paid:      "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-800 border-rose-200",
  canceled:  "bg-rose-50 text-rose-800 border-rose-200",
};

function LinkChip({ kind, record, href, testid }) {
  const label = record?.number || (record?.id ? `#${String(record.id).slice(-6)}` : kind);
  const disabled = record?.deleted;
  const statusCls = STATUS_BADGE[String(record?.status || "").toLowerCase()] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  const inner = (
    <>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{kind}</span>
      <span className="font-mono-accent">{label}</span>
      {record?.status && (
        <span className={`ml-1 text-[9px] uppercase tracking-widest border rounded px-1 ${statusCls}`}>{record.status}</span>
      )}
    </>
  );
  if (disabled) {
    return <span className="text-xs px-2 py-0.5 border border-zinc-200 rounded text-zinc-400 flex items-center gap-1" data-testid={testid} title="Linked record no longer exists">{inner}</span>;
  }
  return (
    <a href={href} className="text-xs px-2 py-0.5 border border-zinc-200 rounded hover:border-[#FF5A00] hover:bg-orange-50 flex items-center gap-1" data-testid={testid}>{inner}</a>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="border border-zinc-200 rounded p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-lg font-heading font-bold mt-0.5">{value}</div>
    </div>
  );
}
